import React, { ChangeEvent, memo, useRef, useState } from 'react';

import { Modal, Button, Input, Space, Select, Form, Tabs, Row, Col, Typography } from 'antd';
import { SurveyType } from 'models/Survey';
import { useNavigate } from 'react-router-dom';
import { EnrollmentFormModel } from 'models/EnrollmentForm';
import { EmailTagInput, FieldData } from 'components/EmailTagInput/EmailTagInput';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import PostalAddress from 'components/PostalAddress';

import SurveyInput from 'components/module/SurveyInput';
import {CeoSurvey} from 'models/CeoSurvey';
import {questions as benchmarkQuestions} from 'routes/CeoSurvey/CeoSurvey.constants';
const {Title} = Typography;


interface StringFunction extends Function {
  (x: string): string;
}

const optionsLabel = (index: number): StringFunction =>
{
  if (index == 15) {
    return x => {
      switch(x) {
        case 'Strongly Disagree':
          return 'Poor';
        case 'Disagree':
          return 'Limited';
        case 'Neutral':
          return 'About Average';
        case 'Agree':
          return 'Good but not Great';
        case 'Strongly Agree':
          return 'Excellent';
        default: return x;
      }
    }
  }

  if (index == 16) {
    return x => {
      switch(x) {
        case 'Strongly Disagree':
          return 'Seldom';
        case 'Disagree':
          return 'Annually';
        case 'Neutral':
          return 'Twice a year';
        case 'Agree':
          return 'Quaterly';
        case 'Strongly Agree':
          return 'Monthly';
        default: return x;
      }
    }
  }

  return x => x;
}

export interface CreateEnrollmentFormModalProps
{
  surveys: SurveyType[];
  isLoading: boolean;
  onSubmit: (data: EnrollmentFormModel, ceoSurvey: CeoSurvey) => void;
}

const CreateEnrollmentFormModal = (props: CreateEnrollmentFormModalProps): JSX.Element =>
{
  const navigate = useNavigate();
  const submitButton = useRef<HTMLButtonElement>(null);
  const [form] = Form.useForm();
  const [enrollmentForm, setEnrollmentForm] = useState([]);
  const [ceoSurvey, setCeoSurvey] = useState(benchmarkQuestions)

  // Functions {{{
  function goBack()
  {
    navigate('/dashboard/enrollment-forms');
  }

  function onFinish(data: any)
  {
    props.onSubmit(data, ceoSurvey);
  }

  function onChangeCeoSurvey(value: string, ndx: number) {
    const newCeoSurvey = [...ceoSurvey];
    newCeoSurvey[ndx] = Object.assign({}, newCeoSurvey[ndx], {value});
    console.log({ newCeoSurvey, ceoSurvey });

    setCeoSurvey(newCeoSurvey);
  }
  // }}}

  return (
    <Modal
    title="Create Enrollment Form"
    centered
    visible
    // onOk={() => {}}
    onCancel={goBack}
    width="70%"
    footer={[
      <Button key="back" onClick={goBack}>Return</Button>,
      <Button
        key="submit"
        type="primary"
        loading={props.isLoading}
        onClick={() => submitButton.current?.click()}
      >
      Submit
      </Button>,
    ]}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Form
          form={form}
          layout="vertical"
          name="enrollmentForm"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          style={{ width: '100%' }}
          onFinishFailed={(err: any) => console.log('onFinishFailed: ', err)}
          >
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="Company" key="1">
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name='companyName' label='Company Name'>
                    <Input/>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name='companyWebsiteUrl' label='Company Website URL'>
                    <Input/>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name='companyAddress' label='Company Address'>
                    <PostalAddress onChange={(companyAddress: any) => form.setFieldsValue({ companyAddress })}/>
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="CEO" key="2">
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    name='ceoName'
                    label='CEO First Name'
                    rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input/>
                  </Form.Item>

                  <Form.Item
                    name='ceoEmail'
                    label='CEO Email (Will receive the Benchmark Information assessment selected below)'
                    rules={[
                      {
                        required: true,
                        type: 'email',
                        message: 'Please enter a valid email'
                      }
                    ]}
                    >
                    <Input/>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name='ceoLastName'
                    label='CEO Last Name'
                    rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input/>
                  </Form.Item>

                  <Form.Item name='ceoContactNumber' label= 'CEO Contact Number'>
                    <PhoneInput
                      placeholder="Enter phone number"
                      onChange={phone => form.setFieldsValue({'ceoContactNumber': phone})}
                      />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Assessment" key="3">
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    name='surveyId'
                    label='Assessment'
                    rules={[{ required: true, message: 'Required' }]}
                    >
                    <Select style={{ width: '100%' }} >
                      {props.surveys.map(x => (
                        <Select.Option value={x.id} key={x.id}>{x.title || 'No Title'}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name='employeeEmails'
                    label='Growth DX Participants Emails (Type email, then press enter)'
                    rules={[{ required: true, message: 'Required' }]}
                    >
                    {
                      <EmailTagInput
                        name='employeeEmails'
                        onChange={(newTags) => {
                          form.setFieldsValue({ employeeEmails: newTags });
                        }}
                        onError={(errors: FieldData[]) => {
                          form.setFields(errors);
                        }}
                        />
                    }
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Benchmark Information" key="4">
              <Row gutter={8}>
                <Col span={24}>
                  {ceoSurvey.filter(({question}) => question !== '')
                  .map(({key, type, question, value}, ndx) => {
                    return (<div key={key}>
                      {
                        ndx < 19 && 
                        <Title level={4}>{ndx + 1}. {question}</Title>
                      }
                      {
                        ndx > 19 && //question === 'What do customers say about your organization? What do you hope they would say?' && 
                        <Title level={4}>{ndx}. {question}</Title>
                      }
                      {
                        ndx === 18 &&
                          <>
                           <span>19 A</span>
                          </>
                      }
                      {
                        ndx === 19 &&
                          <>
                           <span>19 B</span>
                          </>
                      }

                      <SurveyInput
                        type={type}
                        onChange={(val: string) => onChangeCeoSurvey(val, ndx)}
                        optionsLabels={optionsLabel(ndx + 1)}
                        value={value}
                      />
                    </div>)
                  })}
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>
          <button hidden type="submit" ref={submitButton}></button>
        </Form>
      </Space>
    </Modal>
  );
};

export default memo(CreateEnrollmentFormModal);


/**
*/
